import { useTranslations } from "use-intl";
import { SubAccountEarningDto } from "@/models";
import style from "./index.module.scss";

interface IProps {
  earnings: SubAccountEarningDto[];
}

// Buckets the daily earnings rows (GET .../earnings/, one row per day,
// defaults to the last 30 days) into 24h/7d/30d sums on the client - no new
// backend endpoint. There is no 1h bucket: earnings aren't captured at
// intraday granularity anywhere in the backend for any partner, so a "last
// hour" figure would only ever be an estimate, not a measurement - dropped
// per product decision rather than shipped as a fake number.
export function IncomeBreakdown({ earnings }: IProps) {
  const i18n = useTranslations("cabinet.statistics");
  const sorted = [...earnings].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
  const sum = (rows: SubAccountEarningDto[]) =>
    rows.reduce((acc, row) => acc + Number(row.total_profit || 0), 0);

  // Windows are by calendar days back from the newest row, not "the last N
  // rows": a day without an earning row must not stretch the window.
  const lastDays = (days: number) => {
    if (!sorted.length) {
      return [];
    }
    const newest = new Date(sorted[0].date).getTime();
    const cutoff = newest - (days - 1) * 24 * 3600 * 1000;
    return sorted.filter((row) => new Date(row.date).getTime() >= cutoff);
  };

  const rows = [
    { label: i18n("income24h"), value: sum(lastDays(1)) },
    { label: i18n("income7d"), value: sum(lastDays(7)) },
    { label: i18n("income30d"), value: sum(lastDays(30)) },
  ];
  const currency = sorted[0]?.crypto_currency || "";

  return (
    <div className={style.list}>
      {rows.map((row) => (
        <div className={style.row} key={row.label}>
          <span className={style.label}>{row.label}</span>
          <span className={style.value}>
            {row.value.toFixed(8)} {currency}
          </span>
        </div>
      ))}
    </div>
  );
}
